import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';

/**
 * Real-time Platform Events Service
 * Handles real-time subscriptions to critical platform events
 */

export const platformEventsService = {
  /**
   * Subscribe to platform events in real-time
   * @param {Function} onEvent - Callback function for new events
   * @param {Object} options - Subscription options (eventTypes, severity)
   * @returns {Object} Subscription channel
   */
  subscribeToEvents(onEvent, options = {}) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const channelName = `platform_events_${Date.now()}`;
      
      const channel = supabase
        ?.channel(channelName)
        ?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'platform_events'
          },
          (payload) => {
            const event = payload?.new;
            
            // Filter by event type if specified
            if (options?.eventTypes && options?.eventTypes?.length > 0) {
              if (!options?.eventTypes?.includes(event?.event_type)) {
                return;
              }
            }
            
            // Filter by severity if specified
            if (options?.severity && options?.severity?.length > 0) {
              if (!options?.severity?.includes(event?.event_severity)) {
                return;
              }
            }
            
            // Convert snake_case to camelCase
            const formattedEvent = {
              id: event?.id,
              eventType: event?.event_type,
              eventSeverity: event?.event_severity,
              eventCategory: event?.event_category,
              eventData: event?.event_data,
              userId: event?.user_id,
              metadata: event?.metadata,
              acknowledged: event?.acknowledged,
              acknowledgedBy: event?.acknowledged_by,
              acknowledgedAt: event?.acknowledged_at,
              createdAt: event?.created_at
            };
            
            onEvent(formattedEvent);
            logger?.debug('Platform event received', { requestId, eventId: event?.id, eventType: event?.event_type });
          }
        )
        ?.subscribe((status) => {
          logger?.debug('Subscription status', { requestId, status });
        });

      logger?.debug('Subscribed to platform events', { requestId, channelName });
      return channel;
    } catch (error) {
      logger?.error('Platform events subscription error', error, { requestId });
      return null;
    }
  },

  /**
   * Unsubscribe from platform events
   * @param {Object} channel - Subscription channel
   */
  unsubscribe(channel) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      if (channel) {
        supabase?.removeChannel(channel);
        logger?.debug('Unsubscribed from platform events', { requestId });
      }
    } catch (error) {
      logger?.error('Unsubscribe error', error, { requestId });
    }
  },

  /**
   * Get recent platform events
   * @param {Object} options - Query options (limit, eventTypes, severity, timeRange)
   * @returns {Promise<{data, error}>}
   */
  async getRecentEvents(options = {}) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      let query = supabase
        ?.from('platform_events')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      // Filter by event types
      if (options?.eventTypes && options?.eventTypes?.length > 0) {
        query = query?.in('event_type', options?.eventTypes);
      }

      // Filter by severity
      if (options?.severity && options?.severity?.length > 0) {
        query = query?.in('event_severity', options?.severity);
      }

      // Filter by time range (hours)
      if (options?.timeRange) {
        const timeAgo = new Date();
        timeAgo?.setHours(timeAgo?.getHours() - options?.timeRange);
        query = query?.gte('created_at', timeAgo?.toISOString());
      }

      // Limit results
      if (options?.limit) {
        query = query?.limit(options?.limit);
      }

      const { data, error } = await query;

      if (error) {
        logger?.error('Failed to fetch platform events', error, { requestId });
        return { data: null, error };
      }

      // Convert snake_case to camelCase
      const events = data?.map(event => ({
        id: event?.id,
        eventType: event?.event_type,
        eventSeverity: event?.event_severity,
        eventCategory: event?.event_category,
        eventData: event?.event_data,
        userId: event?.user_id,
        metadata: event?.metadata,
        acknowledged: event?.acknowledged,
        acknowledgedBy: event?.acknowledged_by,
        acknowledgedAt: event?.acknowledged_at,
        createdAt: event?.created_at
      }));

      logger?.debug('Platform events fetched', { requestId, count: events?.length });
      return { data: events, error: null };
    } catch (error) {
      logger?.error('Platform events fetch error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch platform events' } };
    }
  },

  /**
   * Get unacknowledged event count
   * @param {Object} options - Query options (eventTypes, severity)
   * @returns {Promise<{data, error}>}
   */
  async getUnacknowledgedCount(options = {}) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: 0, error: { message: 'Not authenticated' } };
      }

      let query = supabase
        ?.from('platform_events')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('acknowledged', false);

      // Filter by event types
      if (options?.eventTypes && options?.eventTypes?.length > 0) {
        query = query?.in('event_type', options?.eventTypes);
      }

      // Filter by severity
      if (options?.severity && options?.severity?.length > 0) {
        query = query?.in('event_severity', options?.severity);
      }

      const { count, error } = await query;

      if (error) {
        logger?.error('Failed to fetch unacknowledged count', error, { requestId });
        return { data: 0, error };
      }

      logger?.debug('Unacknowledged count fetched', { requestId, count });
      return { data: count || 0, error: null };
    } catch (error) {
      logger?.error('Unacknowledged count fetch error', error, { requestId });
      return { data: 0, error: { message: 'Failed to fetch unacknowledged count' } };
    }
  },

  /**
   * Acknowledge a platform event
   * @param {string} eventId - Event ID
   * @returns {Promise<{data, error}>}
   */
  async acknowledgeEvent(eventId) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('platform_events')
        ?.update({
          acknowledged: true,
          acknowledged_by: user?.id,
          acknowledged_at: new Date()?.toISOString()
        })
        ?.eq('id', eventId)
        ?.select()
        ?.single();

      if (error) {
        logger?.error('Failed to acknowledge event', error, { requestId, eventId });
        return { data: null, error };
      }

      logger?.audit('PLATFORM_EVENT_ACKNOWLEDGED', user?.id, { requestId, eventId });
      return { data, error: null };
    } catch (error) {
      logger?.error('Acknowledge event error', error, { requestId, eventId });
      return { data: null, error: { message: 'Failed to acknowledge event' } };
    }
  },

  /**
   * Acknowledge multiple events
   * @param {Array<string>} eventIds - Array of event IDs
   * @returns {Promise<{data, error}>}
   */
  async acknowledgeMultipleEvents(eventIds) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('platform_events')
        ?.update({
          acknowledged: true,
          acknowledged_by: user?.id,
          acknowledged_at: new Date()?.toISOString()
        })
        ?.in('id', eventIds)
        ?.select();

      if (error) {
        logger?.error('Failed to acknowledge multiple events', error, { requestId, count: eventIds?.length });
        return { data: null, error };
      }

      logger?.audit('PLATFORM_EVENTS_ACKNOWLEDGED', user?.id, { requestId, count: eventIds?.length });
      return { data, error: null };
    } catch (error) {
      logger?.error('Acknowledge multiple events error', error, { requestId });
      return { data: null, error: { message: 'Failed to acknowledge events' } };
    }
  },

  /**
   * Get event statistics
   * @param {Object} options - Query options (timeRange)
   * @returns {Promise<{data, error}>}
   */
  async getEventStatistics(options = {}) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      let query = supabase
        ?.from('platform_events')
        ?.select('event_type, event_severity');

      // Filter by time range (hours)
      if (options?.timeRange) {
        const timeAgo = new Date();
        timeAgo?.setHours(timeAgo?.getHours() - options?.timeRange);
        query = query?.gte('created_at', timeAgo?.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        logger?.error('Failed to fetch event statistics', error, { requestId });
        return { data: null, error };
      }

      // Calculate statistics
      const stats = {
        total: data?.length || 0,
        byType: {},
        bySeverity: {
          info: 0,
          warning: 0,
          critical: 0
        }
      };

      data?.forEach(event => {
        // Count by type
        if (!stats?.byType?.[event?.event_type]) {
          stats.byType[event?.event_type] = 0;
        }
        stats.byType[event?.event_type]++;

        // Count by severity
        if (event?.event_severity) {
          stats.bySeverity[event?.event_severity]++;
        }
      });

      logger?.debug('Event statistics calculated', { requestId, total: stats?.total });
      return { data: stats, error: null };
    } catch (error) {
      logger?.error('Event statistics error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch event statistics' } };
    }
  }
};

export default platformEventsService;